import { lazy } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { seoHead, seoHeadFromPage } from "@/config/seoHead";
import { buildTitle } from "@/config/seoTitles";
import { scrapedPageQueryOptions } from "@/hooks/useScrapedPage";
import { lookupSlug, notFoundUnlessRedirected } from "@/lib/slugGuards";

const NewsArticlePage = lazy(() => import("@/pages/NewsArticle"));

export const Route = createFileRoute("/nyheter/$slug")({
  // Nyheten kan ligga antingen i nyhetsarkivet eller bland de dynamiska sidorna.
  loader: async ({ params, context }) => {
    // useContent drar in de statiska nyhets- och rapportarkiven – ladda dem bara här.
    const { newsArticleQueryOptions } = await import("@/hooks/useContent");
    const path = `/nyheter/${params.slug}`;
    const article = await lookupSlug(() => context.queryClient.ensureQueryData(newsArticleQueryOptions(params.slug)));
    if (article) return { article, page: null };
    const page = await lookupSlug(() => context.queryClient.ensureQueryData(scrapedPageQueryOptions(path)));
    // Finns varken i arkivet eller bland de dynamiska sidorna: riktig 404.
    // (undefined = uppslagningen misslyckades – rendera hellre än att ge en falsk 404.)
    if (article === null && page === null) await notFoundUnlessRedirected(path);
    return { article: null, page: page ?? null };
  },
  head: ({ params, loaderData }) => {
    const path = `/nyheter/${params.slug}`;
    if (loaderData?.article) {
      return seoHead(path, {
        title: buildTitle(loaderData.article.title, "article"),
        description: loaderData.article.summary,
        ogType: "article",
        ...(loaderData.article.imageUrl ? { image: loaderData.article.imageUrl } : {}),
        ...(loaderData.article.imageAlt ? { imageAlt: loaderData.article.imageAlt } : {}),
      });
    }
    return seoHeadFromPage(path, loaderData?.page, { ogType: "article" });
  },
  component: () => <NewsArticlePage />,
});
